import { ChangeEvent, FC, FormEvent, useState } from "react";
import { Alert, Button, DialogActions, DialogContent, Stack, TextField, Typography } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useLicenses } from "../contexts/licenseContext";
import useModal from "../hooks/useModal";
import licenseService, { LicenseCheckoutRequest } from "../services/licenseService";
import { formatLicensePrice, licensePriceCents } from "../utils/licenseUtils";

const EMAIL_PATTERN = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

type FieldErrors = {
	producer?: string;
	production?: string;
	email?: string;
};

const LicenseCheckoutForm: FC = () => {
	const { songsInCart } = useLicenses();
	const { hide: hideModal } = useModal();

	const [producer, setProducer] = useState('');
	const [additionalLicensee, setAdditionalLicensee] = useState('');
	const [production, setProduction] = useState('');
	const [email, setEmail] = useState('');
	const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
	const [submitError, setSubmitError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);

	const handleFieldChange = (setter: (value: string) => void) => (event: ChangeEvent<HTMLInputElement>) => {
		setter(event.target.value);
	};

	const validate = (): boolean => {
		const errors: FieldErrors = {};

		if (!producer.trim()) {
			errors.producer = 'A producer name is required.';
		}

		if (!production.trim()) {
			errors.production = 'A production name is required.';
		}

		if (!email.trim()) {
			errors.email = 'Email is required.';
		} else if (!EMAIL_PATTERN.test(email)) {
			errors.email = 'Please enter a valid email address.';
		}

		setFieldErrors(errors);

		return Object.keys(errors).length === 0;
	};

	const handleSubmit = async (event: FormEvent) => {
		event.preventDefault();

		if (!songsInCart.length || !validate()) {
			return;
		}

		setSubmitError(null);
		setSubmitting(true);

		try {
			const request: LicenseCheckoutRequest = {
				songIds: songsInCart.map(song => song.id),
				producer: producer.trim(),
				production: production.trim(),
				email: email.trim()
			};

			const trimmedAdditionalLicensee = additionalLicensee.trim();

			if (trimmedAdditionalLicensee) {
				request.additionalLicensee = trimmedAdditionalLicensee;
			}

			const result = await licenseService.checkoutLicense(request);

			sessionStorage.setItem('tkfm_license_id', result.licenseId);
			window.location.href = result.url;
		} catch (error: any) {
			setSubmitError(error?.message || 'Could not start checkout.');
			setSubmitting(false);
		}
	};

	const handleBackClick = () => {
		hideModal();
	};

	return (
		<form onSubmit={handleSubmit} noValidate>
			<DialogContent>
				<Stack spacing={2}>
					<Typography variant="body2" color="textSecondary">
						{songsInCart.length} tune{songsInCart.length !== 1 ? 's' : ''} &mdash; {formatLicensePrice(licensePriceCents(songsInCart.length))}
					</Typography>
					{submitError && (
						<Alert severity="error">{submitError}</Alert>
					)}
					<TextField
						name="producer"
						label="Production Company"
						required
						fullWidth
						variant="standard"
						value={producer}
						onChange={handleFieldChange(setProducer)}
						error={!!fieldErrors.producer}
						helperText={fieldErrors.producer}
						disabled={submitting}
					/>
					<TextField
						name="additionalLicensee"
						label="Additional Licensee"
						fullWidth
						variant="standard"
						value={additionalLicensee}
						onChange={handleFieldChange(setAdditionalLicensee)}
						helperText="(optional)"
						disabled={submitting}
					/>
					<TextField
						name="production"
						label="Title of the Production"
						required
						fullWidth
						variant="standard"
						value={production}
						onChange={handleFieldChange(setProduction)}
						error={!!fieldErrors.production}
						helperText={fieldErrors.production}
						disabled={submitting}
					/>
					<TextField
						name="email"
						label="Email"
						required
						fullWidth
						variant="standard"
						value={email}
						onChange={handleFieldChange(setEmail)}
						error={!!fieldErrors.email}
						helperText={fieldErrors.email || 'Your license PDF download link will be sent here.'}
						disabled={submitting}
					/>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button variant="text" onClick={handleBackClick} disabled={submitting}>
					Back to Cart
				</Button>
				<Button
					type="submit"
					variant="contained"
					color="primary"
					disabled={submitting}
					startIcon={submitting ? <FontAwesomeIcon icon={{ prefix: 'fas', iconName: 'spinner' }} spin /> : undefined}
				>
					{submitting ? 'Redirecting to Payment…' : 'Continue to Payment'}
				</Button>
			</DialogActions>
		</form>
	);
};

export default LicenseCheckoutForm;
